/* ============================================================
   Game View — three hierarchy layouts.
     1. GvTreeLayout    — expandable tree table (primary)
     2. GvMasterDetail  — player list + detail pane
     3. GvSplitLayout   — concentration panels side-by-side
   Each takes { players, max, live, fixtureId }.
   ============================================================ */
import React, { useState } from 'react';
import {
  gvK, gvLine, gvPct, gvDominant,
  gvRollMarket, gvRollPlayer,
  GvLi, GvSideMetric, GvSideMetricCell, GvComboChip, GvGateBadge, gvObHref,
} from './gameViewUtils';

function isYesOnlyMarket(marketId) {
  const id = String(marketId || '').toLowerCase();
  return id.includes('goalscorer') || id.includes('to_score');
}

function sideFlow(row) {
  const nOver = row.n_entries_over ?? row.n_over ?? 0;
  const nUnder = row.n_entries_under ?? row.n_under ?? 0;
  return {
    nOver,
    nUnder,
    sharpOver: row.avg_sharp_over,
    sharpUnder: row.avg_sharp_under,
    avgSlipOver: row.avg_slip_over,
    avgSlipUnder: row.avg_slip_under,
  };
}

/* =================================================================
   LAYOUT 1 — EXPANDABLE TREE TABLE
   ================================================================= */
export function GvTreeLayout({ players, max, live, fixtureId }) {
  const [openP, setOpenP] = useState(() => new Set());
  const [openM, setOpenM] = useState(() => new Set());

  const toggleP = (id) => setOpenP((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleM = (k) => setOpenM((s) => { const n = new Set(s); n.has(k) ? n.delete(k) : n.add(k); return n; });
  const expandAll = () => {
    setOpenP(new Set(players.map((p) => p.id)));
    setOpenM(new Set(players.flatMap((p) => p.markets.map((m) => `${p.id}::${m.id}`))));
  };
  const collapseAll = () => {
    setOpenP(new Set());
    setOpenM(new Set());
  };

  return (
    <>
      <div className="gv-tree-controls">
        <button type="button" className="gv-tree-control" onClick={expandAll}>
          Expand all
        </button>
        <button type="button" className="gv-tree-control" onClick={collapseAll}>
          Collapse all
        </button>
      </div>
      <div className="gv-table-wrap">
        <table className="gv-table">
          <thead>
            <tr>
              <th className="gv-expand-col" />
              <th>Player / Market / Line</th>
              <th>Status</th>
              <th className="num">P(fair)</th>
              <th className="num">Handle</th>
              <th className="num" title="Net liability if OVER hits (positive = book exposed)">Liab Over</th>
              <th className="num" title="Net liability if UNDER hits">Liab Under</th>
              <th className="num" title="Worst-case dominant leg">Worst</th>
              <th className="num">Sharp O / U</th>
              <th className="num">Entries O / U</th>
              <th className="num">Avg slip O / U</th>
              <th title="Combo legs touching this row">Combo</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {players.map((p) => {
            const rp = gvRollPlayer(p);
            const fp = sideFlow(rp);
            const pOpen = openP.has(p.id);
            return (
              <React.Fragment key={p.id}>
                <tr className="gv-row-player" onClick={() => toggleP(p.id)}>
                  <td className="gv-expand-col">
                    <button className={`gv-expand-btn${pOpen ? ' open' : ''}`} aria-label="expand">▸</button>
                  </td>
                  <td>
                    <span className="gv-player-name">{p.name}</span>
                    <span className="gv-player-pos">{p.pos}</span>
                    <span className="gv-player-pos">{p.team}</span>
                  </td>
                  <td className="gv-muted">{p.markets.length} mkts</td>
                  <td className="num gv-muted">—</td>
                  <td className="num gv-num">{gvK(rp.fee)}</td>
                  <td className="num"><GvLi v={rp.lo} max={max} /></td>
                  <td className="num"><GvLi v={rp.lu} max={max} /></td>
                  <td className="num"><GvLi v={rp.dom} max={max} /></td>
                  <GvSideMetricCell over={fp.sharpOver} under={fp.sharpUnder} format={(v) => v.toFixed(2)} sharp />
                  <GvSideMetricCell over={fp.nOver} under={fp.nUnder} />
                  <GvSideMetricCell over={fp.avgSlipOver} under={fp.avgSlipUnder} format={gvK} />
                  <td>{rp.combo ? <GvComboChip n={rp.combo} /> : <span className="gv-muted">—</span>}</td>
                  <td />
                </tr>

                {pOpen && p.markets.map((m) => {
                  const rm = gvRollMarket(m);
                  const fm = sideFlow(rm);
                  const mk = `${p.id}::${m.id}`;
                  const mOpen = openM.has(mk);
                  return (
                    <React.Fragment key={mk}>
                      <tr className="gv-row-market" onClick={() => toggleM(mk)}>
                        <td className="gv-expand-col" />
                        <td className="gv-indent-1">
                          <button className={`gv-expand-btn${mOpen ? ' open' : ''}`} aria-label="expand" style={{ marginRight: '0.3rem' }}>▸</button>
                          <span className="gv-market-name">{m.name}</span>
                          <span className="gv-muted" style={{ fontSize: '0.62rem', marginLeft: '0.4rem' }}>{m.lines.length} lines</span>
                        </td>
                        <td className="gv-muted" style={{ fontSize: '0.62rem' }}>{m.id}</td>
                        <td className="num gv-muted">—</td>
                        <td className="num gv-num">{gvK(rm.fee)}</td>
                        <td className="num"><GvLi v={rm.lo} max={max} /></td>
                        <td className="num"><GvLi v={rm.lu} max={max} /></td>
                        <td className="num"><GvLi v={rm.dom} max={max} /></td>
                        <GvSideMetricCell over={fm.sharpOver} under={fm.sharpUnder} format={(v) => v.toFixed(2)} sharp />
                        <GvSideMetricCell over={fm.nOver} under={fm.nUnder} />
                        <GvSideMetricCell over={fm.avgSlipOver} under={fm.avgSlipUnder} format={gvK} />
                        <td>{rm.combo ? <GvComboChip n={rm.combo} /> : <span className="gv-muted">—</span>}</td>
                        <td />
                      </tr>

                      {mOpen && m.lines.map((ln) => {
                        const dom = gvDominant(ln.liability_net_over, ln.liability_net_under);
                        const fl = sideFlow(ln);
                        const isGoalscorer = isYesOnlyMarket(m.id);
                        return (
                          <tr className="gv-row-line" key={`${mk}::${ln.line}`}>
                            <td className="gv-expand-col" />
                            <td className="gv-indent-2">
                              <span className="gv-line-label">{isGoalscorer ? 'Yes' : gvLine(ln.line)}</span>
                              {ln.quote && <span className="gv-quote-pill">quote</span>}
                            </td>
                            <td><GvGateBadge gate={ln.gate} /></td>
                            <td className="num gv-num">{gvPct(ln.p_fair)}</td>
                            <td className="num gv-num" title={`Over ${gvK(ln.fee_over)} · Under ${gvK(ln.fee_under)}`}>
                              {gvK(ln.fee_over + ln.fee_under)}
                            </td>
                            <td className="num"><GvLi v={ln.liability_net_over} max={max} /></td>
                            <td className="num"><GvLi v={ln.liability_net_under} max={max} /></td>
                            <td className="num"><GvLi v={dom} max={max} /></td>
                            <GvSideMetricCell over={fl.sharpOver} under={fl.sharpUnder} format={(v) => v.toFixed(2)} sharp />
                            <GvSideMetricCell over={fl.nOver} under={fl.nUnder} />
                            <GvSideMetricCell over={fl.avgSlipOver} under={fl.avgSlipUnder} format={gvK} />
                            <td>{ln.combo ? <GvComboChip n={ln.combo} /> : <span className="gv-muted">—</span>}</td>
                            <td>
                              <a className="gv-ob-link" href={gvObHref(fixtureId, p.id, m.id, ln.line)}>
                                Market dashboard ›
                              </a>
                            </td>
                          </tr>
                        );
                      })}
                    </React.Fragment>
                  );
                })}
              </React.Fragment>
            );
            })}
          </tbody>
        </table>
      </div>
    </>
  );
}

/* =================================================================
   LAYOUT 2 — MASTER / DETAIL
   ================================================================= */
export function GvMasterDetail({ players, max, fixtureId }) {
  const [sel, setSel] = useState(players[3] ? players[3].id : players[0].id);
  const player = players.find((p) => p.id === sel) || players[0];
  const rp = gvRollPlayer(player);
  const fp = sideFlow(rp);

  return (
    <div className="gv-md">
      <div className="gv-md-list">
        {[...players].sort((a, b) => gvRollPlayer(b).dom - gvRollPlayer(a).dom).map((p) => {
          const r = gvRollPlayer(p);
          const f = sideFlow(r);
          return (
            <div key={p.id} className={`gv-md-item${p.id === sel ? ' gv-md-item--on' : ''}`} onClick={() => setSel(p.id)}>
              <div className="gv-md-item-main">
                <div>
                  <div className="gv-md-item-name">{p.name}</div>
                  <div className="gv-md-item-meta">
                    {p.pos} · {p.team} · {gvK(r.fee)} handle · O {f.nOver} / U {f.nUnder} entries
                  </div>
                </div>
              </div>
              <div className="gv-md-item-li">
                <GvLi v={r.dom} max={max} />
                {r.combo ? <div style={{ marginTop: 2 }}><GvComboChip n={r.combo} /></div> : null}
              </div>
            </div>
          );
        })}
      </div>

      <div className="gv-md-detail">
        <div className="gv-md-detail-head">
          <span className="gv-player-name" style={{ fontSize: '1rem' }}>{player.name}</span>
          <span className="gv-player-pos">{player.pos}</span>
          <span className="gv-player-pos">{player.team}</span>
          <span style={{ marginLeft: 'auto', display: 'flex', gap: '1.1rem', alignItems: 'baseline' }}>
            <span className="gv-mini">Handle <strong className="gv-num">{gvK(rp.fee)}</strong></span>
            <span className="gv-mini">Worst <GvLi v={rp.dom} max={max} /></span>
            <span className="gv-mini">Sharp <GvSideMetric over={fp.sharpOver} under={fp.sharpUnder} format={(v) => v.toFixed(2)} sharp /></span>
            <span className="gv-mini">Entries <GvSideMetric over={fp.nOver} under={fp.nUnder} /></span>
            <span className="gv-mini">Avg slip <GvSideMetric over={fp.avgSlipOver} under={fp.avgSlipUnder} format={gvK} /></span>
          </span>
        </div>
        <div className="gv-table-wrap">
          <table className="gv-table">
            <thead>
              <tr>
                <th>Market / Line</th>
                <th>Status</th>
                <th className="num">P(fair)</th>
                <th className="num">Handle</th>
                <th className="num">Liab Over</th>
                <th className="num">Liab Under</th>
                <th className="num">Sharp O / U</th>
                <th className="num">Entries O / U</th>
                <th className="num">Avg slip O / U</th>
                <th>Combo</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {player.markets.map((m) => {
                const rm = gvRollMarket(m);
                const fm = sideFlow(rm);
                return (
                  <React.Fragment key={m.id}>
                    <tr className="gv-row-market">
                      <td><span className="gv-market-name">{m.name}</span></td>
                      <td className="gv-muted" style={{ fontSize: '0.62rem' }}>{m.lines.length} lines</td>
                      <td className="num gv-muted">—</td>
                      <td className="num gv-num">{gvK(rm.fee)}</td>
                      <td className="num"><GvLi v={rm.lo} max={max} /></td>
                      <td className="num"><GvLi v={rm.lu} max={max} /></td>
                      <GvSideMetricCell over={fm.sharpOver} under={fm.sharpUnder} format={(v) => v.toFixed(2)} sharp />
                      <GvSideMetricCell over={fm.nOver} under={fm.nUnder} />
                      <GvSideMetricCell over={fm.avgSlipOver} under={fm.avgSlipUnder} format={gvK} />
                      <td>{rm.combo ? <GvComboChip n={rm.combo} /> : <span className="gv-muted">—</span>}</td>
                      <td />
                    </tr>
                    {m.lines.map((ln) => {
                      const isGoalscorer = isYesOnlyMarket(m.id);
                      const fl = sideFlow(ln);
                      return (
                        <tr className="gv-row-line" key={`${m.id}::${ln.line}`}>
                          <td className="gv-indent-1">
                            <span className="gv-line-label">{isGoalscorer ? 'Yes' : gvLine(ln.line)}</span>
                            {ln.quote && <span className="gv-quote-pill">quote</span>}
                          </td>
                          <td><GvGateBadge gate={ln.gate} /></td>
                          <td className="num gv-num">{gvPct(ln.p_fair)}</td>
                          <td className="num gv-num">{gvK(ln.fee_over + ln.fee_under)}</td>
                          <td className="num"><GvLi v={ln.liability_net_over} max={max} /></td>
                          <td className="num"><GvLi v={ln.liability_net_under} max={max} /></td>
                          <GvSideMetricCell over={fl.sharpOver} under={fl.sharpUnder} format={(v) => v.toFixed(2)} sharp />
                          <GvSideMetricCell over={fl.nOver} under={fl.nUnder} />
                          <GvSideMetricCell over={fl.avgSlipOver} under={fl.avgSlipUnder} format={gvK} />
                          <td>{ln.combo ? <GvComboChip n={ln.combo} /> : <span className="gv-muted">—</span>}</td>
                          <td>
                            <a className="gv-ob-link" href={gvObHref(fixtureId, player.id, m.id, ln.line)}>
                              Market dashboard ›
                            </a>
                          </td>
                        </tr>
                      );
                    })}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

/* =================================================================
   LAYOUT 3 — CONCENTRATION SPLIT PANELS
   ================================================================= */
export function GvSplitLayout({ players, max, fixtureId }) {
  const legs = [];
  for (const p of players) {
    for (const m of p.markets) {
      for (const ln of m.lines) {
        const dom = gvDominant(ln.liability_net_over, ln.liability_net_under);
        legs.push({ p, m, ln, dom, abs: Math.abs(dom) });
      }
    }
  }
  const byRisk = [...legs].sort((a, b) => b.dom - a.dom).slice(0, 8);
  const byCombo = [...legs].filter((l) => l.ln.combo > 0).sort((a, b) => b.ln.combo - a.ln.combo).slice(0, 8);
  const topAbs = byRisk.length ? Math.max(...byRisk.map((l) => Math.abs(l.dom)), 1) : 1;
  const topCombo = byCombo.length ? byCombo[0].ln.combo : 1;

  const LegCard = ({ leg, metric }) => {
    const isGoalscorer = isYesOnlyMarket(leg.m.id);
    const pct = metric === 'combo' ? leg.ln.combo / topCombo : leg.abs / topAbs;
    const barColor = metric === 'combo' ? 'var(--combo)' : (leg.dom > 0 ? 'var(--li-pos)' : 'var(--li-neg)');
    const flow = sideFlow(leg.ln);
    return (
      <a className="gv-mini-card" href={gvObHref(fixtureId, leg.p.id, leg.m.id, leg.ln.line)} style={{ textDecoration: 'none', color: 'inherit', display: 'block' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: '0.5rem' }}>
          <div className="gv-md-item-main" style={{ minWidth: 0 }}>
            <div className="gv-md-item-name" style={{ fontSize: '0.78rem' }}>{leg.p.name}</div>
            <div className="gv-md-item-meta">{leg.m.name} · {isGoalscorer ? 'Yes' : gvLine(leg.ln.line)}</div>
          </div>
          <div style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
            {metric === 'combo'
              ? <span className="gv-combo-chip"><span className="dot" />{leg.ln.combo} legs</span>
              : <GvLi v={leg.dom} max={max} />}
          </div>
        </div>
        <div className="gv-mini-card-flow">
          <span>Sharp <GvSideMetric over={flow.sharpOver} under={flow.sharpUnder} format={(v) => v.toFixed(2)} sharp /></span>
          <span>Entries <GvSideMetric over={flow.nOver} under={flow.nUnder} /></span>
          <span>Avg <GvSideMetric over={flow.avgSlipOver} under={flow.avgSlipUnder} format={gvK} /></span>
        </div>
        <div className="gv-bar" style={{ width: `${Math.max(6, pct * 100)}%`, background: barColor }} />
      </a>
    );
  };

  return (
    <div className="gv-split">
      <div className="gv-panel">
        <div className="gv-panel-title">
          Where risk is piling up
          <span className="gv-mini">worst-case net liability</span>
        </div>
        {byRisk.map((leg, i) => <LegCard key={i} leg={leg} metric="risk" />)}
      </div>
      <div className="gv-panel">
        <div className="gv-panel-title">
          Most-combined legs
          <span className="gv-mini">player legs inside combos</span>
        </div>
        {byCombo.map((leg, i) => <LegCard key={i} leg={leg} metric="combo" />)}
      </div>
    </div>
  );
}
